import type { Module, Badge, Lesson } from '@/types/course';
import { module1 } from './course/module-1';
import { module2 } from './course/module-2';
import { module3 } from './course/module-3';
import { module4 } from './course/module-4';
import { module5 } from './course/module-5';
import { module6 } from './course/module-6';
import { module7 } from './course/module-7';

export { module1, module2, module3, module4, module5, module6, module7 };

export const courseModules: Module[] = [
  module1,
  module2,
  module3,
  module4,
  module5,
  module6,
  module7,
];

export const TOTAL_LESSONS = courseModules.reduce(
  (sum, m) => sum + m.lessons.length,
  0,
);
export const TOTAL_MODULES = courseModules.length;
export const QUIZ_PASS_THRESHOLD = 0.8;
export const TOTAL_DURATION_MINUTES = courseModules.reduce(
  (sum, m) => sum + m.durationMinutes,
  0,
);

export const badges: Badge[] = [
  {
    slug: 'primer-paso',
    name: 'Primer Paso',
    description: 'Completaste tu primera lección.',
    trigger: 'first_lesson_complete',
    icon: 'Footprints',
  },
  {
    slug: 'historiador',
    name: 'Historiador del Oro',
    description: 'Módulo 1 completado: dominas la historia y rol macro del oro.',
    trigger: 'module_1_complete',
    icon: 'BookOpen',
  },
  {
    slug: 'conocedor-lingotes',
    name: 'Conocedor de Lingotes',
    description:
      'Módulo 2 completado: distingues lingotes, monedas bullion y numismática.',
    trigger: 'module_2_complete',
    icon: 'Coins',
  },
  {
    slug: 'comprador-astuto',
    name: 'Comprador Astuto',
    description:
      'Módulo 3 completado: sabes comprar oro de forma segura y fiscalmente óptima.',
    trigger: 'module_3_complete',
    icon: 'ShieldCheck',
  },
  {
    slug: 'custodio-seguro',
    name: 'Custodio Seguro',
    description:
      'Módulo 4 completado: dominas las opciones de custodia y seguros.',
    trigger: 'module_4_complete',
    icon: 'Lock',
  },
  {
    slug: 'estratega',
    name: 'Estratega',
    description:
      'Módulo 5 completado: tienes un marco mental claro para invertir en oro.',
    trigger: 'module_5_complete',
    icon: 'Compass',
  },
  {
    slug: 'vendedor-inteligente',
    name: 'Vendedor Inteligente',
    description:
      'Módulo 6 completado: sabes vender oro maximizando valor y fiscalidad.',
    trigger: 'module_6_complete',
    icon: 'TrendingUp',
  },
  {
    slug: 'numismatico',
    name: 'Numismático',
    description:
      'Módulo 7 completado: sabes identificar monedas que no deben fundirse.',
    trigger: 'module_7_complete',
    icon: 'Coins',
  },
  {
    slug: 'maestro-oro',
    name: 'Maestro del Oro',
    description:
      'Curso completo + todos los quizzes aprobados. Has alcanzado el certificado de Oro Express.',
    trigger: 'course_complete',
    icon: 'Trophy',
  },
];

export function findModuleBySlug(slug: string): Module | undefined {
  return courseModules.find((m) => m.slug === slug);
}

export function findLesson(
  moduleSlug: string,
  lessonSlug: string,
): { module: Module; lesson: Lesson; index: number } | null {
  const mod = findModuleBySlug(moduleSlug);
  if (!mod) return null;
  const idx = mod.lessons.findIndex((l) => l.slug === lessonSlug);
  if (idx === -1) return null;
  return { module: mod, lesson: mod.lessons[idx]!, index: idx };
}

export function getNextLesson(
  moduleSlug: string,
  lessonSlug: string,
): { moduleSlug: string; lessonSlug: string } | null {
  const found = findLesson(moduleSlug, lessonSlug);
  if (!found) return null;
  const { module: mod, index } = found;

  if (index + 1 < mod.lessons.length) {
    return { moduleSlug, lessonSlug: mod.lessons[index + 1]!.slug };
  }
  const modIdx = courseModules.findIndex((m) => m.slug === moduleSlug);
  const nextMod = courseModules[modIdx + 1];
  if (!nextMod) return null;
  return { moduleSlug: nextMod.slug, lessonSlug: nextMod.lessons[0]!.slug };
}

export function getPrevLesson(
  moduleSlug: string,
  lessonSlug: string,
): { moduleSlug: string; lessonSlug: string } | null {
  const found = findLesson(moduleSlug, lessonSlug);
  if (!found) return null;
  const { index } = found;

  if (index > 0) {
    return {
      moduleSlug,
      lessonSlug: courseModules
        .find((m) => m.slug === moduleSlug)!
        .lessons[index - 1]!.slug,
    };
  }
  const modIdx = courseModules.findIndex((m) => m.slug === moduleSlug);
  const prevMod = courseModules[modIdx - 1];
  if (!prevMod) return null;
  return {
    moduleSlug: prevMod.slug,
    lessonSlug: prevMod.lessons[prevMod.lessons.length - 1]!.slug,
  };
}

export function findBadgeBySlug(slug: string): Badge | undefined {
  return badges.find((b) => b.slug === slug);
}
